import type { ComposeAttachment } from "@/mail/composeDraft";

export type InlineExtractResult = {
  html: string;
  inline: ComposeAttachment[];
};

function dataUrlToBase64(dataUrl: string): { mimeType: string; contentBase64: string } | null {
  const match = /^data:([^;,]+?)(?:;charset=[^;,]+)?;base64,([\s\S]+)$/i.exec(dataUrl.trim());
  if (!match) return null;
  const mimeType = (match[1] || "image/png").trim() || "image/png";
  const contentBase64 = (match[2] || "").replace(/\s+/g, "");
  if (!contentBase64) return null;
  return { mimeType, contentBase64 };
}

function extForMime(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("gif")) return "gif";
  if (m.includes("webp")) return "webp";
  if (m.includes("bmp")) return "bmp";
  return "png";
}

function filenameNearImgTag(tag: string, index: number, mime: string): string {
  const dataName = /\bdata-filename\s*=\s*(["'])([^"']*)\1/i.exec(tag)?.[2]?.trim();
  if (dataName) return dataName;
  const alt = /\balt\s*=\s*(["'])([^"']*)\1/i.exec(tag)?.[2]?.trim();
  if (alt && /\.[a-z0-9]{2,5}$/i.test(alt)) return alt;
  return `image-${index}.${extForMime(mime)}`;
}

/**
 * Tip 193: turn compose &lt;img src="data:…"&gt; into cid: inline parts for MIME send.
 * Uses regex (not DOMParser) so Node selftests and send-time extraction stay aligned.
 */
export function extractInlineImagesFromHtml(html: string): InlineExtractResult {
  if (!html || !/data:image\//i.test(html)) {
    return { html, inline: [] };
  }
  const inline: ComposeAttachment[] = [];
  let index = 0;
  const nextHtml = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const srcMatch = /\bsrc\s*=\s*(["'])(data:image\/[^"']+)\1/i.exec(tag);
    if (!srcMatch) return tag;
    const quote = srcMatch[1]!;
    const dataUrl = srcMatch[2]!;
    const parsed = dataUrlToBase64(dataUrl);
    if (!parsed) return tag;
    index += 1;
    const contentId = `hub-inline-${index}@atw.hub`;
    const filename = filenameNearImgTag(tag, index, parsed.mimeType);
    const size = Math.round((parsed.contentBase64.length * 3) / 4);
    inline.push({
      filename,
      mimeType: parsed.mimeType,
      contentBase64: parsed.contentBase64,
      size,
      contentId,
      inline: true,
    });
    return tag
      .replace(srcMatch[0], `src=${quote}cid:${contentId}${quote}`)
      .replace(/\sdata-hub-selected\s*=\s*(["'])[^"']*\1/i, "");
  });
  return { html: nextHtml, inline };
}
